import {
  DEFAULT_DOMAIN_NAMES,
  QUADRANT_CELLS,
  QUADRANT_ORDER,
  cellRect,
  mapFrame,
  mapLabel,
  guideLines,
  historyDots,
  plotPoint,
  trailLine,
  type Scores,
} from "../lib/map";
import type { DomainCode, OutputRecord, QuadrantName } from "../lib/types";

// Colours: as on the homepage (Dane's), the map itself is neutral and only the person is teal:
// the dot, its halo and guides, and their quadrant's name. No domain corners (Edward, Oct 22):
// the domains sit on a different map (task to people, thinking to doing), so corners here implied
// a link between a domain and a quadrant that doesn't exist.
const ACCENT_FILL = "var(--color-accent)";
const ACCENT = "var(--color-accent-text)";
const INK = "var(--color-ink)";
const MUTED = "var(--color-muted)";
const LINE = "var(--color-line)";

export type MapRecord = Pick<
  OutputRecord,
  "freedom" | "clarity" | "quadrant" | "borderline" | "primary_domain" | "secondary_domain" | "balanced" | "domain_counts"
>;

/** Taylor's map: the 2x2 and the dot. */
export function ResultMap({
  record,
  domainNames = DEFAULT_DOMAIN_NAMES,
  before,
  history,
}: {
  /** The current (or "after") position, quadrant and domains. */
  record: MapRecord;
  domainNames?: Partial<Record<DomainCode, string>>;
  /** Event series: the "before" position, drawn muted with a line to the current dot. */
  before?: (Scores & { quadrant?: QuadrantName }) | null;
  /** Earlier takes (newest first), drawn as small muted dots. Ignored when `before` is set. */
  history?: Scores[] | null;
}) {
  const earlierDots = !before && history ? historyDots(history) : [];
  const f = mapFrame();
  const { left, top, side } = f;
  const mid = { x: left + side / 2, y: top + side / 2 };
  const dot = plotPoint(record.clarity, record.freedom, f);
  const guides = guideLines(record.clarity, record.freedom, f);
  const label = mapLabel(record, domainNames, before);
  const beforeDot = before ? plotPoint(before.clarity, before.freedom, f) : null;
  const trail = before ? trailLine(before, record, f) : null;
  // The frame keeps its margin (every point is placed in it); the view crops to the grid and its axis labels.
  const view = { x: left - 30, y: top - 24, w: side + 60, h: side + 48 };

  return (
    <svg
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      role="img"
      aria-label={label}
      className="block h-auto w-full max-w-[360px]"
      style={{ fontFamily: "var(--font-sans)" }}
    >
      <rect x={left} y={top} width={side} height={side} rx={16} fill="var(--color-panel)" />

      {QUADRANT_ORDER.map((q) => {
        const r = cellRect(QUADRANT_CELLS[q], f);
        const mine = q === record.quadrant;
        return (
          <g key={q} data-quadrant={q} data-selected={mine || undefined}>
            <text
              x={r.x + r.width / 2}
              y={r.y + (QUADRANT_CELLS[q].startsWith("top") ? 22 : r.height - 14)}
              textAnchor="middle"
              fontSize={11}
              fontWeight={mine ? 600 : 500}
              letterSpacing="0.08em"
              fill={mine ? ACCENT : MUTED}
            >
              {q.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* The cross at the midline */}
      <line x1={mid.x} y1={top} x2={mid.x} y2={top + side} stroke={LINE} strokeWidth={1} />
      <line x1={left} y1={mid.y} x2={left + side} y2={mid.y} stroke={LINE} strokeWidth={1} />
      <rect x={left} y={top} width={side} height={side} rx={16} fill="none" stroke={LINE} />

      {/* Axis ends */}
      <g fontSize={10} fontWeight={600} letterSpacing="0.1em" fill={MUTED}>
        <text x={mid.x} y={top - 8} textAnchor="middle">
          FREE
        </text>
        <text x={mid.x} y={top + side + 17} textAnchor="middle">
          BOUND
        </text>
        <text x={left - 10} y={mid.y} textAnchor="middle" transform={`rotate(-90 ${left - 10} ${mid.y})`}>
          UNCLEAR
        </text>
        <text x={left + side + 10} y={mid.y} textAnchor="middle" transform={`rotate(90 ${left + side + 10} ${mid.y})`}>
          CLEAR
        </text>
      </g>

      {/* Dashed guides from the dot to the left (Freedom) and bottom (Clarity) edges */}
      <g data-testid="map-guides" stroke={ACCENT} strokeOpacity={0.6} strokeWidth={1.25} strokeDasharray="3 3" aria-hidden="true">
        <line {...guides.toLeft} />
        <line {...guides.toBottom} />
      </g>

      {/* Earlier takes: small muted dots, newest darkest */}
      {earlierDots.map((d, i) => (
        <circle key={i} data-testid="history-dot" cx={d.x} cy={d.y} r={4.5} fill={MUTED} fillOpacity={d.opacity} />
      ))}

      {/* Event series: the "before" dot (muted outline) and a thin arrow to the current dot */}
      {beforeDot ? (
        <g data-testid="before-dot" data-x={beforeDot.x} data-y={beforeDot.y}>
          <defs>
            <marker id="map-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={MUTED} />
            </marker>
          </defs>
          {trail ? (
            <line
              data-testid="before-trail"
              x1={trail.x1}
              y1={trail.y1}
              x2={trail.x2}
              y2={trail.y2}
              stroke={MUTED}
              strokeWidth={1.5}
              markerEnd="url(#map-arrow)"
            />
          ) : null}
          <rect
            x={beforeDot.x - 6}
            y={beforeDot.y - 6}
            width={12}
            height={12}
            rx={2}
            fill="var(--color-panel)"
            stroke={MUTED}
            strokeWidth={2}
          />
        </g>
      ) : null}

      {/* The dot: a solid accent square with a soft halo; a dashed ring when borderline */}
      <g data-testid="map-dot" data-x={dot.x} data-y={dot.y}>
        <circle cx={dot.x} cy={dot.y} r={15} fill={ACCENT_FILL} fillOpacity={0.18} />
        {record.borderline ? (
          <circle
            data-testid="borderline-ring"
            cx={dot.x}
            cy={dot.y}
            r={24}
            fill="none"
            stroke={ACCENT}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        ) : null}
        {/* Taylor's dot: a filled circle with a thin ground-coloured ring. */}
        <circle cx={dot.x} cy={dot.y} r={7} fill={ACCENT} stroke="var(--color-panel)" strokeWidth={2} />
      </g>
    </svg>
  );
}

/**
 * Small 2x2 glyph for the end of the test. `at` moves the dot away from the centre (in the
 * glyph's own units) and `trail` leaves faint dots where it has been, for the finish screen's
 * "finding your spot" wander.
 */
export function QuadrantGlyph({ at, trail = [] }: { at?: { x: number; y: number }; trail?: { x: number; y: number }[] } = {}) {
  return (
    <svg viewBox="0 0 148 148" width={128} height={128} aria-hidden="true">
      <rect x={0.5} y={0.5} width={147} height={147} rx={14} fill="var(--color-panel)" stroke={LINE} />
      <line x1={74} y1={0.5} x2={74} y2={147.5} stroke={LINE} />
      <line x1={0.5} y1={74} x2={147.5} y2={74} stroke={LINE} />
      {trail.map((p, i) => (
        <circle key={i} cx={74 + p.x} cy={74 + p.y} r={4} fill={ACCENT_FILL} fillOpacity={0.08 * (i + 1)} />
      ))}
      <g className="glyph-dot" style={at ? { transform: `translate(${at.x}px, ${at.y}px)` } : undefined}>
        <circle cx={74} cy={74} r={22} fill={ACCENT_FILL} fillOpacity={0.12} />
        <circle cx={74} cy={74} r={15} fill={ACCENT_FILL} fillOpacity={0.15} />
        <circle cx={74} cy={74} r={7} fill={ACCENT_FILL} />
      </g>
    </svg>
  );
}
